import { useCallback } from 'react';
import { ttqIdentify, ttqPage, ttqTrack } from './tiktok';
import { fbqTrack } from './meta';
import { useBasket } from '../hooks/useBasket';

export const useAnalytics = () => {
  const { basket, totalQuantity, totalAmount } = useBasket();

  const addToCart = useCallback(
    ({
      id,
      quantity,
      unitPrice,
    }: {
      id: number;
      quantity: number;
      unitPrice: number;
    }) => {
      const value = quantity * unitPrice;

      // TikTok
      ttqTrack('AddToCart', {
        value,
        currency: 'TRY',
        contents: [
          {
            content_id: id.toString(),
            quantity,
            price: unitPrice,
          },
        ],
      });

      // Meta
      fbqTrack('AddToCart', {
        content_ids: [id.toString()],
        content_type: 'product',
        value,
        currency: 'TRY',
      });
    },
    [],
  );

  const addToWishlist = useCallback(({ id }: { id: number }) => {
    // TikTok
    ttqTrack('AddToWishlist', {
      content_ids: [id.toString()],
    });

    // Meta
    fbqTrack('AddToWishlist', {
      content_ids: [id.toString()],
      content_type: 'product',
    });
  }, []);

  const completeRegistration = useCallback(
    ({ email, phone }: { email: string; phone: string }) => {
      // TikTok (allowed)
      ttqTrack('CompleteRegistration', {
        email,
        phone,
      });

      // Meta (no PII client-side)
      fbqTrack('CompleteRegistration');
    },
    [],
  );

  const viewContent = useCallback(
    ({
      contentType,
      slug,
    }: {
      contentType: 'product' | 'product_group';
      slug: string;
    }) => {
      // TikTok
      ttqTrack('ViewContent', {
        content_type: contentType,
        content_id: slug,
      });

      // Meta
      fbqTrack('ViewContent', {
        content_ids: [slug],
        content_type: contentType,
      });
    },
    [],
  );

  const initiateCheckout = useCallback(() => {
    const items =
      basket?.items.map((item) => ({
        content_id: item.product.id.toString(),
        quantity: Number(item.quantity ?? 0),
        price: Number(item.total?.amount ?? 0),
      })) ?? [];

    if (items.length === 0) return;

    const value = items.reduce((sum, it) => sum + it.price * it.quantity, 0);

    // TikTok
    ttqTrack('InitiateCheckout', {
      contents: items,
      value,
      currency: 'TRY',
    });

    // Meta
    fbqTrack('InitiateCheckout', {
      content_ids: items.map((i) => i.content_id),
      value,
      currency: 'TRY',
      num_items: items.length,
    });
  }, [basket]);

  const purchase = useCallback(() => {
    if (!basket || basket.items.length === 0) return;

    // TikTok
    ttqTrack('Purchase', {
      content_type: 'product',
      quantity: totalQuantity,
      description: 'Purchase',
      content_ids: basket.items.map((item) => item.product.id.toString()),
      currency: 'TRY',
      value: totalAmount,
    });

    // Meta
    fbqTrack('Purchase', {
      content_ids: basket.items.map((item) => item.product.id.toString()),
      content_type: 'product',
      value: totalAmount,
      currency: 'TRY',
      num_items: totalQuantity,
    });
  }, [basket, totalQuantity, totalAmount]);

  const page = useCallback(() => {
    ttqPage();
  }, []);

  const identify = useCallback(({ email }: { email: string }) => {
    ttqIdentify({ email });
  }, []);

  return {
    addToCart,
    addToWishlist,
    completeRegistration,
    viewContent,
    initiateCheckout,
    purchase,
    page,
    identify,
  };
};
